export type QualityFlowchartEdgeType =
  | 'smoothstep'
  | 'bezier'
  | 'step'
  | 'straight'

export type QualityFlowchartStatus = 'draft' | 'published'

export interface QualityFlowchartGraph {
  nodes: unknown[]
  edges: unknown[]
  viewport: { x: number; y: number; zoom: number } | null
  edgeType?: QualityFlowchartEdgeType
}

export interface QualityFlowchart {
  id: string
  title: string
  description: string | null
  flow_json: QualityFlowchartGraph
  status: QualityFlowchartStatus
  created_by?: string | null
  updated_by?: string | null
  created_at: string
  updated_at: string
  current_version?: number | null
  created_by_name?: string
  updated_by_name?: string
  /** Preenchido na exclusão lógica */
  deleted_at?: string | null
  deleted_by?: string | null
}

export interface QualityFlowchartVersion {
  id: string
  flowchart_id: string
  version_number: number
  title: string
  description: string | null
  flow_json: QualityFlowchartGraph
  created_at: string
  created_by?: string | null
  change_summary?: string | null
  user_name?: string
}

export interface CreateQualityFlowchartPayload {
  title: string
  description?: string | null
  flow_json?: QualityFlowchartGraph
}

export interface UpdateQualityFlowchartPayload {
  title?: string
  description?: string | null
  flow_json?: QualityFlowchartGraph
  /** When true, creates a new version (published saves). Draft updates must pass false. */
  createVersion?: boolean
  change_summary?: string
}

export interface QualityFlowchartFilters {
  query?: string
  status?: QualityFlowchartStatus
  sort_by?: 'title' | 'updated_at' | 'created_at'
  sort_order?: 'asc' | 'desc'
}

export const EMPTY_FLOWCHART_GRAPH: QualityFlowchartGraph = {
  nodes: [],
  edges: [],
  viewport: null,
  edgeType: 'smoothstep',
}

export function normalizeFlowchartStatus(
  value: unknown
): QualityFlowchartStatus {
  return value === 'published' ? 'published' : 'draft'
}

/** Converte flow_json do banco (objeto ou string JSON) em grafo usável. */
export function normalizeGraph(raw: unknown): QualityFlowchartGraph {
  let data: unknown = raw
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch {
      return { ...EMPTY_FLOWCHART_GRAPH }
    }
  }
  if (!data || typeof data !== 'object') {
    return { ...EMPTY_FLOWCHART_GRAPH }
  }
  const obj = data as Record<string, unknown>
  return {
    nodes: Array.isArray(obj.nodes) ? obj.nodes : [],
    edges: Array.isArray(obj.edges) ? obj.edges : [],
    viewport:
      obj.viewport && typeof obj.viewport === 'object'
        ? (obj.viewport as QualityFlowchartGraph['viewport'])
        : null,
    edgeType:
      obj.edgeType === 'bezier' ||
      obj.edgeType === 'step' ||
      obj.edgeType === 'straight' ||
      obj.edgeType === 'smoothstep'
        ? obj.edgeType
        : 'smoothstep',
  }
}

/**
 * Grafo limpo e serializável para gravar no JSONB.
 * Remove campos internos do React Flow que podem corromper o payload.
 */
export function sanitizeFlowGraph(
  raw: Partial<QualityFlowchartGraph> & { name?: string }
): QualityFlowchartGraph {
  const normalized = normalizeGraph(raw)
  const nodes = normalized.nodes.map((node) => {
    const n = node as Record<string, unknown>
    return {
      id: String(n.id ?? ''),
      type: n.type,
      position:
        n.position && typeof n.position === 'object'
          ? {
              x: Number((n.position as { x?: number }).x) || 0,
              y: Number((n.position as { y?: number }).y) || 0,
            }
          : { x: 0, y: 0 },
      parentId: n.parentId,
      width: n.width,
      height: n.height,
      extent: n.extent,
      expandParent: n.expandParent,
      style: n.style && typeof n.style === 'object' ? n.style : undefined,
      data:
        n.data && typeof n.data === 'object'
          ? n.data
          : { label: '' },
    }
  })
  const edges = normalized.edges.map((edge) => {
    const e = edge as Record<string, unknown>
    return {
      id: String(e.id ?? ''),
      source: String(e.source ?? ''),
      target: String(e.target ?? ''),
      sourceHandle: e.sourceHandle ?? undefined,
      targetHandle: e.targetHandle ?? undefined,
      type: e.type ?? 'labeled',
      markerEnd: e.markerEnd,
      data: e.data && typeof e.data === 'object' ? e.data : { label: '' },
    }
  })
  return {
    nodes,
    edges,
    viewport: normalized.viewport,
    edgeType: normalized.edgeType ?? 'smoothstep',
  }
}
